import type { BitmapData } from "../../runtime";
import {
  G_HL1,
  G_HL2,
  G_HL3,
  G_back,
  G_menuBox,
  G_menuBoxNarrow,
  G_menuBoxSmall,
} from "../assets";
import { FONT_BODY, FONT_DISPLAY } from "../textStyle";
import type { SceneName } from "./scenes";

/** Constructor of one of the generated bitmap asset classes. */
export type BitmapSource = new () => BitmapData;

/** Bitmap set and text layout an entry is drawn with. */
export type MenuBoxKind =
  "title item" | "menu item" | "level select" | "value select" | "score";

export type MenuColour = "white" | "aqua" | "red";

/** Whether an entry becomes a selectable option, a cycler widget, or plain text. */
export type MenuRole = "option" | "choice" | "label";

/** Text layout shared by every entry of one box kind. */
export interface MenuKind {
  readonly font: string;
  readonly size: number;
  readonly txtWidth: number;
  readonly txtY: number;
  readonly align: "center" | "right";
  readonly role: MenuRole;
  /** left/right arrow pair as `[distance from centre, y offset]` */
  readonly arrows?: readonly [number, number];
}

export const MENU_KINDS: Readonly<Record<MenuBoxKind, MenuKind>> = {
  "title item": {
    font: FONT_BODY,
    size: 18,
    txtWidth: 240,
    txtY: 12,
    align: "center",
    role: "option",
  },
  "menu item": {
    font: FONT_DISPLAY,
    size: 24,
    txtWidth: 240,
    txtY: 12,
    align: "center",
    role: "option",
  },
  "level select": {
    font: FONT_DISPLAY,
    size: 50,
    txtWidth: 96,
    txtY: 12,
    align: "center",
    role: "choice",
    arrows: [58, 45],
  },
  "value select": {
    font: FONT_DISPLAY,
    size: 20,
    txtWidth: 150,
    txtY: 12,
    align: "center",
    role: "choice",
    arrows: [80, 26],
  },
  score: {
    font: FONT_DISPLAY,
    size: 24,
    txtWidth: 240,
    txtY: 12,
    align: "right",
    role: "label",
  },
};

/** Bitmaps and cycler behaviour of one `menu item`, keyed by its label. */
export interface MenuItemBox {
  readonly highlight: BitmapSource;
  readonly box: BitmapSource;
  readonly highlightX?: number;
  readonly highlightY?: number;
  readonly txtWidth?: number;
  readonly txtY?: number;
  /** 0 plain, 1 mission cycler, 2 sound cycler, 3 cheat cycler; `Menus.update` dispatches on it */
  readonly optionId?: number;
  /** cycler length, or "unlocked" for one step per unlocked mission */
  readonly choiceTotal?: number | "unlocked";
  /** starting cycler value, "selected" for the current mission, "cheats" for the cheat toggle */
  readonly choiceCurrent?: number | "selected" | "cheats";
  readonly canPress?: boolean;
}

/** Values used by every menu item that does not override them. */
export const MENU_ITEM_DEFAULTS = {
  highlightX: -53,
  highlightY: 7,
  txtWidth: 240,
  txtY: 12,
  optionId: 0,
  choiceTotal: 1,
  choiceCurrent: "selected",
  canPress: true,
} as const;

/** The narrow box the three plain main-menu entries share. */
const NARROW_BOX: MenuItemBox = {
  highlight: G_HL2,
  box: G_menuBoxNarrow,
  txtWidth: 188,
  txtY: 5,
};

export const MENU_ITEM_BOXES: Readonly<
  Record<string, MenuItemBox | undefined>
> = {
  "MISSION SELECT": {
    highlight: G_HL3,
    box: G_menuBoxSmall,
    highlightX: -58,
    highlightY: -1,
    optionId: 1,
    choiceTotal: "unlocked",
  },
  "GO BACK": { highlight: G_HL2, box: G_back, highlightY: 20 },
  "GAME START": { highlight: G_HL1, box: G_menuBox },
  // the sound cycler always starts at 1 even when the saved state is not "FX/MUSIC ON" - original quirk
  SOUND: {
    highlight: G_HL2,
    box: G_menuBoxNarrow,
    optionId: 2,
    choiceTotal: 4,
    choiceCurrent: 1,
    canPress: false,
    txtWidth: 188,
    txtY: 5,
  },
  CHEATS: {
    highlight: G_HL2,
    box: G_menuBoxNarrow,
    optionId: 3,
    choiceTotal: 2,
    choiceCurrent: "cheats",
    canPress: false,
    txtWidth: 188,
    txtY: 5,
  },
  SURVIVAL: NARROW_BOX,
  "MORE GAMES": NARROW_BOX,
  "WIPE DATA": NARROW_BOX,
};

/** What selecting an option makes Game do once the flash animation finishes. */
export interface MenuSelect {
  /** scene Game should switch to */
  readonly goto?: SceneName;
  /** open the sponsor site instead of leaving the scene */
  readonly website?: boolean;
  /** side-effect code handed back to Game; 1 opens the wipe-data confirm box */
  readonly gotoAction?: number;
  /** return the option's cycler value to Game as `extraValue` */
  readonly passChoice?: boolean;
  /** grow the knight's map base while the transition plays */
  readonly expandBase?: boolean;
}

/** One `generateText` call: a label plus where and how it is placed. */
export interface MenuEntry {
  readonly x: number;
  /** stage y, or an offset from the row cursor when `stacked` */
  readonly y: number;
  /** `{sound}`, `{cheats}`, `{score}` and `{level}` are substituted when the screen is built */
  readonly text: string;
  readonly kind: MenuBoxKind;
  readonly colour?: MenuColour;
  /** `y` is relative to the running row cursor rather than the stage */
  readonly stacked?: boolean;
  /** advance the row cursor by `MENU_ROW_SPACING` once this entry is placed */
  readonly endsRow?: boolean;
  /** placed only once survival mode is unlocked */
  readonly survivalOnly?: boolean;
  /** placed only once the cheat menu is unlocked */
  readonly cheatsOnly?: boolean;
  readonly select?: MenuSelect;
}

/** First stacked main-menu row, and the gap between rows. */
export const MENU_ROW_START = 175;
export const MENU_ROW_SPACING = 48;

/** Sound-state label, indexed by `Menus.soundState`. */
// prettier-ignore
export const SOUND_STATE_TEXT: readonly string[] = ["FX/MUSIC ON", "FX/MUSIC ON", "FX ONLY", "MUSIC ONLY", "OFF"];

// prettier-ignore
const TITLE_SCREEN: readonly MenuEntry[] = [
  { x: 59, y: 372, text: "CLICK / TAP / SPACE", kind: "title item", select: { goto: "main menu" } },
];

// prettier-ignore
const MAIN_MENU_SCREEN: readonly MenuEntry[] = [
  { x: 55, y: 109, text: "GAME START", kind: "menu item", select: { goto: "level select" } },
  { x: 55, y: 0, stacked: true, survivalOnly: true, text: "SURVIVAL", kind: "menu item", select: { goto: "survival" } },
  { x: 150, y: -8, stacked: true, survivalOnly: true, text: "TOP SCORE:", kind: "score" },
  { x: 255, y: -8, stacked: true, survivalOnly: true, endsRow: true, text: "{score}", kind: "score" },
  { x: 55, y: 0, stacked: true, text: "SOUND", kind: "menu item" },
  { x: 285, y: -3, stacked: true, endsRow: true, text: "{sound}", kind: "value select", colour: "aqua" },
  { x: 55, y: 0, stacked: true, endsRow: true, text: "MORE GAMES", kind: "menu item", select: { website: true } },
  { x: 55, y: 0, stacked: true, text: "WIPE DATA", kind: "menu item", select: { gotoAction: 1 } },
  { x: 285, y: 0, stacked: true, cheatsOnly: true, text: "CHEATS", kind: "menu item" },
  { x: 505, y: -3, stacked: true, cheatsOnly: true, text: "{cheats}", kind: "value select", colour: "aqua" },
  { x: 55, y: 380, text: "GO BACK", kind: "menu item", select: { goto: "opening" } },
];

// prettier-ignore
const LEVEL_SELECT_SCREEN: readonly MenuEntry[] = [
  { x: 57, y: 101, text: "MISSION SELECT", kind: "menu item", select: { goto: "level", passChoice: true, expandBase: true } },
  { x: 57, y: 89, text: "{level}", kind: "level select", colour: "aqua" },
  { x: 14, y: 204, text: "MORE GAMES", kind: "menu item", select: { website: true } },
  { x: 14, y: 364, text: "GO BACK", kind: "menu item", select: { goto: "main menu" } },
];

/**
 * Menu entries per scene, in the order the original built them. Option indices
 * are handed out in this order and Game switches on them, so entries must not
 * be reordered; the `select` field keeps each index tied to its action even
 * when the survival entry shifts everything below it down one slot.
 */
export const MENU_SCREENS: Readonly<
  Partial<Record<SceneName, readonly MenuEntry[]>>
> = {
  title: TITLE_SCREEN,
  "main menu": MAIN_MENU_SCREEN,
  "level select": LEVEL_SELECT_SCREEN,
};
